// Pure, dependency-free model helpers for the PLC edge-runtimes page.
// Deliberately free of React/JSX so the enrollment, sync-policy, GPIO board,
// PLC-sync, build-identity, and command-deadline logic stays unit-testable and
// shared between the page (platform-edge-runtimes.js) and its modal/detail
// components (platformEdgeRuntimes.components.js).

const _gwConfig = (typeof window !== "undefined" && window.CS_GATEWAY_CONFIG) || {};
const CORE_URL = _gwConfig.CORE_URL || process.env.REACT_APP_CORE_URL || "http://localhost:8090";

export const CUSTOM_URL = "__custom__";

// Generic is the safe enrollment default. OnLogic profiles enable bounded
// model-specific package and device policies and therefore always require an
// explicit operator choice.
export const AUTO_DETECT_HARDWARE_PROFILE = "auto-detect";
export const DEFAULT_EDGE_HARDWARE_PROFILE = AUTO_DETECT_HARDWARE_PROFILE;
export const DEFAULT_EDGE_CLOUD_TRANSPORT = "websocket";

export const EDGE_CLOUD_TRANSPORT_OPTIONS = Object.freeze([
  Object.freeze({ value: "websocket", label: "WebSocket (default)" }),
  Object.freeze({ value: "mqtt", label: "MQTT" }),
]);

export const normalizeEdgeCloudTransport = (value) => (
  String(value || "").trim().toLowerCase() === "mqtt" ? "mqtt" : "websocket"
);

export const EDGE_RUNTIME_HARDWARE_PROFILES = Object.freeze([
  Object.freeze({
    value: AUTO_DETECT_HARDWARE_PROFILE,
    label: "Other Linux PC",
    product: "Generic Linux hardware",
    summary: "Use for an unlisted PC. Control Seat detects available hardware, but does not enable built-in I/O automatically.",
    operatingSystem: "64-bit Linux",
    ioSummary: "External protocol connections; choose the exact model to enable its built-in I/O",
    syntheticAutomatic: true,
  }),
  Object.freeze({
    value: "onlogic-fr201-v1",
    // Both Factor profiles are Control Seat-supplied machines, so both carry
    // the Control Seat name. `product` keeps the vendor model designation,
    // which is what a technician reads off the physical case.
    label: "Control Seat FR201",
    product: "OnLogic Factor 201",
    summary: "OnLogic Factor 201 profile with its built-in industrial serial port.",
    operatingSystem: "Ubuntu Server 22.04 64-bit for FR200",
    ioSummary: "Built-in RS-232/422/485 · external protocol connections",
  }),
  Object.freeze({
    value: "onlogic-fr202-v1",
    label: "Control Seat FR202",
    product: "OnLogic Factor 202",
    summary: "Preconfigured profile for Control Seat-supplied OnLogic Factor 202 IPCs.",
    operatingSystem: "Ubuntu Server 22.04 64-bit for FR200",
    ioSummary: "16 digital inputs · 16 read/write digital outputs · 4 analog inputs · RTD inputs not enabled",
    hardwareContract: "controlseat.fr202.runtime-io.v1",
    terminalCatalogVersion: 1,
  }),
  Object.freeze({
    value: "compulab-iot-link-v1",
    label: "Compulab IOT-LINK",
    product: "IOT-LINK LTE/RS-485",
    summary: "Compact industrial gateway with LTE, Ethernet, serial, and local digital I/O.",
    operatingSystem: "Debian 12 Bookworm ARM64",
    ioSummary: "2 half-duplex RS-485 ports · 3 configurable 24 V digital channels",
  }),
  Object.freeze({
    value: "generic-linux-ipc-v1",
    label: "Generic Linux IPC",
    product: "Industrial PC or VM",
    summary: "Use when the customer brings their own 64-bit Linux host.",
    operatingSystem: "64-bit systemd 249+ Linux (Debian 12, Ubuntu 22.04+, or equivalent)",
    ioSummary: "Protocol connectors only until the runtime advertises qualified hardware I/O",
  }),
  Object.freeze({
    value: "raspberry-pi-header-gpio-v1",
    label: "Raspberry Pi header GPIO",
    product: "Raspberry Pi 4/5 lab profile",
    summary: "Small bench/lab GPIO profile. Not an OnLogic terminal-block I/O profile.",
    operatingSystem: "64-bit Raspberry Pi OS Bookworm or Ubuntu 22.04+",
    ioSummary: "3.3 V BCM header pins only",
  }),
  Object.freeze({
    value: "teltonika-rutc-v1",
    label: "Teltonika RUTC router",
    product: "RUTC40/41/42/50 on RutOS",
    summary: "Cellular edge router running the runtime in a Debian 12 container next to RutOS.",
    operatingSystem: "RutOS 7.16+ with Docker, Debian 12 ARM64 container",
    ioSummary: "Network protocols over the router's LAN, Wi-Fi, and VPN uplinks",
  }),
]);

// RutOS has no bash or sudo: Teltonika routers get a busybox-sh script that
// prepares the Debian container and runs the ordinary setup inside it.
export const isRutOSHardwareProfile = (value) => String(value || "").trim() === "teltonika-rutc-v1";

export const EDGE_RUNTIME_HARDWARE_PROFILE_OPTIONS = Object.freeze(
  EDGE_RUNTIME_HARDWARE_PROFILES.map((profile) => Object.freeze({
    value: profile.value,
    label: profile.label,
  })),
);

const publicHardwareProfile = (profile) => {
  if (!profile || typeof profile !== "object") return null;
  const value = String(profile.value || profile.id || profile.profile || profile.hardware_profile || "").trim();
  const label = String(profile.label || profile.name || "").trim();
  if (!value || !label || value === AUTO_DETECT_HARDWARE_PROFILE) return null;
  return {
    value,
    label,
    product: String(profile.product || profile.model || label).trim(),
    summary: String(profile.summary || profile.description || "").trim(),
    operatingSystem: String(profile.operating_system || profile.operatingSystem || "").trim(),
    ioSummary: String(profile.io_summary || profile.ioSummary || "").trim(),
    setupMode: String(profile.setup_mode || profile.setupMode || "").trim(),
    builtInIO: Boolean(profile.built_in_io ?? profile.builtInIO),
    automaticDetection: Boolean(profile.automatic_detection ?? profile.automaticDetection),
  };
};

// The profile endpoint lets the server add supported hardware without a
// frontend release. A missing/older endpoint safely falls back to the
// checked-in catalog used by existing deployments.
export const normalizeHardwareProfileCatalog = (payload) => {
  const source = Array.isArray(payload) ? payload : Array.isArray(payload?.profiles) ? payload.profiles : [];
  const dynamic = source.map(publicHardwareProfile).filter(Boolean);
  const fallback = EDGE_RUNTIME_HARDWARE_PROFILES.filter((profile) => profile.value !== AUTO_DETECT_HARDWARE_PROFILE);
  const merged = new Map(fallback.map((profile) => [profile.value, profile]));
  dynamic.forEach((profile) => merged.set(profile.value, { ...merged.get(profile.value), ...profile }));
  return [EDGE_RUNTIME_HARDWARE_PROFILES[0], ...merged.values()];
};

export const hardwareProfileOptions = (catalog = EDGE_RUNTIME_HARDWARE_PROFILES) => (
  (Array.isArray(catalog) ? catalog : EDGE_RUNTIME_HARDWARE_PROFILES).map((profile) => ({
    value: profile.value,
    label: profile.label,
  }))
);

// The current installer uses the generic Linux package for discovery. The UI
// keeps automatic detection distinct from an operator explicitly choosing
// Generic Linux, while the existing enrollment wire contract stays backward
// compatible.
export const enrollmentHardwareProfile = (value) => (
  value === AUTO_DETECT_HARDWARE_PROFILE ? "generic-linux-ipc-v1" : value
);

export const edgeHardwareProfileByID = (value) => {
  const id = String(value || "").trim();
  return EDGE_RUNTIME_HARDWARE_PROFILES.find((profile) => profile.value === id) || null;
};

export const edgeHardwareProfileFromCatalog = (value, catalog) => {
  const id = String(value || "").trim();
  return (Array.isArray(catalog) ? catalog : EDGE_RUNTIME_HARDWARE_PROFILES)
    .find((profile) => profile.value === id) || edgeHardwareProfileByID(id);
};

export const runtimeIOPresentation = (hardwareProfile) => {
  const profile = typeof hardwareProfile === "string"
    ? edgeHardwareProfileByID(hardwareProfile)
    : hardwareProfile;
  switch (profile?.value) {
    case "onlogic-fr202-v1":
      return {
        description: "Review the built-in digital I/O or add a supported protocol connection.",
        aiTask: "Help me connect FR202 digital I/O, Modbus, EtherNet/IP, OPC UA, or MQTT I/O",
        hardwareSetup: true,
      };
    case "onlogic-fr201-v1":
      return {
        description: "Use the built-in industrial serial port or add a supported Ethernet connection. This model does not have built-in terminal I/O.",
        aiTask: "Help me connect the FR201 serial port, Modbus, EtherNet/IP, OPC UA, or MQTT I/O",
        hardwareSetup: false,
      };
    case "compulab-iot-link-v1":
      return {
        description: "Use either built-in RS-485 port, its three digital channels, or a supported Ethernet connection.",
        aiTask: "Help me connect IOT-LINK digital I/O, RS-485, Modbus TCP, OPC UA, or MQTT I/O",
        hardwareSetup: false,
      };
    case "raspberry-pi-header-gpio-v1":
      return {
        description: "Add Raspberry Pi header GPIO or a supported protocol connection.",
        aiTask: "Help me connect Raspberry Pi GPIO, Modbus, OPC UA, or MQTT I/O",
        hardwareSetup: false,
      };
    default:
      return {
        description: "Add a protocol connection supported by this runtime.",
        aiTask: "Help me connect Modbus, EtherNet/IP, OPC UA, or MQTT I/O",
        hardwareSetup: false,
      };
  }
};

const HARDWARE_STATUS_COPY = Object.freeze({
  detected: { label: "Hardware detected", tone: "isPositive", next: "Review the detected interfaces before using them." },
  configured: { label: "Configured", tone: "isPositive", next: "Built-in hardware is ready to use." },
  setup_required: { label: "Setup required", tone: "isWarning", next: "Review how the detected interface is wired before connecting tags." },
  calibration_required: { label: "Calibration required", tone: "isWarning", next: "Calibrate this channel before using its measurements." },
  driver_required: { label: "Driver required", tone: "isWarning", next: "This interface needs a supported driver before it can be used." },
  changed: { label: "Hardware changed", tone: "isWarning", next: "Review the detected hardware. Existing connections were left unchanged." },
  none: { label: "No local I/O detected", tone: "isNeutral", next: "You can still add supported protocol connections." },
  unknown: { label: "Not reported", tone: "isNeutral", next: "Connect this runtime to detect its local hardware." },
});

const normalizeHardwareStatus = (value) => {
  const raw = String(value || "").trim().toLowerCase().replace(/[ -]+/g, "_");
  const aliases = {
    ready: "configured",
    matched: "detected",
    exact_match: "detected",
    needs_setup: "setup_required",
    needs_calibration: "calibration_required",
    update_required: "driver_required",
    mismatch: "changed",
    no_hardware: "none",
    unavailable: "none",
  };
  const normalized = aliases[raw] || raw;
  return Object.prototype.hasOwnProperty.call(HARDWARE_STATUS_COPY, normalized) ? normalized : "unknown";
};

const publicHardwareGroup = (group, index) => {
  if (!group || typeof group !== "object") return null;
  const label = String(group.label || group.name || group.kind || "").trim().replace(/_/g, " ");
  if (!label) return null;
  const countValue = Number(group.count ?? group.point_count ?? group.points?.length);
  return {
    key: String(group.key || group.kind || `group-${index}`),
    label,
    detail: String(group.summary || group.detail || group.description || "").trim(),
    count: Number.isFinite(countValue) && countValue >= 0 ? countValue : null,
    status: normalizeHardwareStatus(group.status || group.readiness || "configured"),
  };
};

const publicHardwareResource = (resource, index) => {
  if (!resource || typeof resource !== "object") return null;
  const label = String(resource.label || resource.name || resource.kind || resource.type || "").trim().replace(/_/g, " ");
  if (!label) return null;
  return {
    key: String(resource.key || `resource-${index}`),
    label,
    detail: String(resource.summary || resource.detail || resource.description || "").trim(),
    status: normalizeHardwareStatus(resource.status || resource.readiness || "detected"),
  };
};

const legacyHardwareInventory = (gateway) => {
  const runtime = normalizeRuntime(gateway?.runtime);
  const profile = edgeHardwareProfileByID(gateway?.hardware_profile || runtime?.hardware_profile);
  const fr202 = runtime?.fr202_io;
  const fr202Groups = Array.isArray(fr202?.terminal_groups) ? fr202.terminal_groups : [];
  if (fr202 || profile?.value === "onlogic-fr202-v1") {
    return {
      profile,
      status: fr202 ? "configured" : "driver_required",
      summary: fr202 ? "Built-in digital I/O and the analog/RTD interface." : "Update this runtime to detect its built-in I/O.",
      groups: fr202Groups.map((group, index) => publicHardwareGroup({
        ...group,
        status: group.kind === "analog_input"
          ? "calibration_required"
          : group.kind === "rtd_input" ? "driver_required" : "configured",
      }, index)).filter(Boolean),
      resources: [],
    };
  }
  const serialPorts = Array.isArray(runtime?.serial_ports) ? runtime.serial_ports : [];
  if (profile?.value === "onlogic-fr201-v1" || profile?.value === "compulab-iot-link-v1") {
    const iotLink = profile.value === "compulab-iot-link-v1";
    const gpioDetected = iotLink && runtime?.gpio_board?.status === "detected";
    const resources = [];
    if (serialPorts.length) resources.push({
      key: "serial",
      label: iotLink ? "RS-485 ports" : "Industrial serial port",
      detail: iotLink ? "Two half-duplex ports" : "RS-232, RS-422, or RS-485",
      status: "setup_required",
    });
    if (gpioDetected) resources.push({
      key: "digital_io",
      label: "Digital I/O",
      detail: "Three configurable 24 V channels",
      status: "setup_required",
    });
    return {
      profile,
      status: serialPorts.length && (!iotLink || gpioDetected) ? "setup_required" : "driver_required",
      summary: serialPorts.length
        ? iotLink
          ? gpioDetected
            ? "Both RS-485 ports and all three digital channels were detected. Confirm field wiring before use."
            : "Both RS-485 ports were detected, but the digital I/O controller was not."
          : "Built-in industrial serial hardware detected. Confirm its electrical mode and wiring before use."
        : iotLink
          ? "The two RS-485 ports were not detected."
          : "The built-in industrial serial hardware has not been detected.",
      groups: [],
      resources,
    };
  }
  const gpio = runtime?.gpio_board;
  if (gpio?.status === "detected") {
    return {
      profile,
      status: "setup_required",
      summary: "Compatible GPIO hardware detected.",
      groups: [],
      resources: [{ key: "gpio", label: String(gpio.profile_label || "GPIO header"), detail: "Choose a pin and its electrical behavior before use.", status: "setup_required" }],
    };
  }
  if (serialPorts.length) {
    return {
      profile,
      status: "detected",
      summary: `${serialPorts.length} serial ${serialPorts.length === 1 ? "interface" : "interfaces"} detected.`,
      groups: [],
      resources: [{ key: "serial", label: "Serial interfaces", detail: `${serialPorts.length} detected`, status: "detected" }],
    };
  }
  return { profile, status: gateway?.last_seen_at ? "none" : "unknown", summary: "", groups: [], resources: [] };
};

// Normalize the generic hardware endpoint into a deliberately small public
// view. Raw device paths, fingerprints, driver errors, and profile machinery
// are intentionally discarded here so they cannot leak into rendering.
export const runtimeHardwareView = (payload, gateway) => {
  const legacy = legacyHardwareInventory(gateway);
  const available = payload && typeof payload === "object" && !Array.isArray(payload);
  const profileValue = available ? payload.profile : null;
  const profile = profileValue && typeof profileValue === "object"
    ? publicHardwareProfile(profileValue)
    : edgeHardwareProfileByID(profileValue) || legacy.profile;
  const groups = available && Array.isArray(payload.terminal_groups)
    ? payload.terminal_groups.map(publicHardwareGroup).filter(Boolean)
    : legacy.groups;
  const resources = available && Array.isArray(payload.resources)
    ? payload.resources.map(publicHardwareResource).filter(Boolean)
    : legacy.resources;
  const inferredStatus = available
    ? normalizeHardwareStatus(payload.status || (groups.length || resources.length ? "detected" : "none"))
    : legacy.status;
  const stale = Boolean(payload?.stale) || gateway?.status === "offline";
  const copy = HARDWARE_STATUS_COPY[inferredStatus];
  return {
    available,
    profile,
    status: inferredStatus,
    statusLabel: stale && inferredStatus !== "unknown" ? "Offline · last detected" : copy.label,
    tone: stale && inferredStatus !== "unknown" ? "isWarning" : copy.tone,
    summary: String(payload?.summary || legacy.summary || copy.next).trim(),
    next: stale ? "Reconnect this runtime to refresh its hardware status." : copy.next,
    stale,
    truncated: payload?.truncated === true,
    reportedAt: String(payload?.reported_at || ""),
    groups,
    resources,
  };
};

export const runtimeSyncClassOptions = (hardwareProfile) => {
  const profile = typeof hardwareProfile === "string"
    ? edgeHardwareProfileByID(hardwareProfile)
    : hardwareProfile;
  const options = [["includePLC", "PLC values"]];
  if (profile?.value === "onlogic-fr202-v1") options.push(["includeGPIO", "Built-in I/O values"]);
  if (profile?.value === "raspberry-pi-header-gpio-v1") options.push(["includeGPIO", "GPIO values"]);
  options.push(["includeConnectors", "Connection values"]);
  options.push(["includeOutputEvidence", "Output confirmation"]);
  return options;
};

export const DEFAULT_EDGE_SYNC_POLICY = Object.freeze({
  retentionSeconds: 24 * 60 * 60,
  eventCapacity: 10000,
  byteCapacity: 256 * 1024 * 1024,
  snapshotSeconds: 10,
  includeGPIO: true,
  includePLC: true,
  includeConnectors: true,
  includeOutputEvidence: true,
});

export const normalizeEdgeSyncPolicy = (value) => {
  const policy = value && typeof value === "object" ? value : {};
  return {
    retentionSeconds: Number(policy.retention_seconds ?? policy.retentionSeconds) || DEFAULT_EDGE_SYNC_POLICY.retentionSeconds,
    eventCapacity: Number(policy.event_capacity ?? policy.eventCapacity) || DEFAULT_EDGE_SYNC_POLICY.eventCapacity,
    byteCapacity: Number(policy.byte_capacity ?? policy.byteCapacity) || DEFAULT_EDGE_SYNC_POLICY.byteCapacity,
    snapshotSeconds: Number(policy.snapshot_seconds ?? policy.snapshotSeconds) || DEFAULT_EDGE_SYNC_POLICY.snapshotSeconds,
    includeGPIO: policy.include_gpio ?? policy.includeGPIO ?? DEFAULT_EDGE_SYNC_POLICY.includeGPIO,
    includePLC: policy.include_plc ?? policy.includePLC ?? DEFAULT_EDGE_SYNC_POLICY.includePLC,
    includeConnectors: policy.include_connectors ?? policy.includeConnectors ?? DEFAULT_EDGE_SYNC_POLICY.includeConnectors,
    includeOutputEvidence: policy.include_output_evidence ?? policy.includeOutputEvidence ?? DEFAULT_EDGE_SYNC_POLICY.includeOutputEvidence,
  };
};

export const edgeGatewayEnrollmentPayload = ({ name, installID, hardwareProfile, syncPolicy, cloudTransport }) => {
  const policy = normalizeEdgeSyncPolicy(syncPolicy);
  return {
    name: String(name || "").trim(),
    install_id: String(installID || "").trim(),
    hardware_profile: String(enrollmentHardwareProfile(hardwareProfile) || "").trim(),
    cloud_transport: normalizeEdgeCloudTransport(cloudTransport),
    edge_event_sync_policy: {
      retention_seconds: policy.retentionSeconds,
      event_capacity: policy.eventCapacity,
      byte_capacity: policy.byteCapacity,
      snapshot_seconds: policy.snapshotSeconds,
      include_gpio: policy.includeGPIO,
      include_plc: policy.includePLC,
      include_connectors: policy.includeConnectors,
      include_output_evidence: policy.includeOutputEvidence,
    },
  };
};

// The reviewed recovery endpoint deliberately accepts the same complete
// enrollment contract as create, plus the dedicated durable settings revision
// the operator reviewed. It never accepts a partial policy: omitting a
// field here could silently restore a server default while rotating the only
// usable bootstrap credential.
export const edgeGatewayEnrollmentSettingsPayload = ({ gateway, hardwareProfile, syncPolicy, cloudTransport }) => {
  const policy = normalizeEdgeSyncPolicy(syncPolicy);
  const expectedRevision = gateway?.enrollment_settings_revision;
  return {
    hardware_profile: String(enrollmentHardwareProfile(hardwareProfile) || "").trim(),
    cloud_transport: normalizeEdgeCloudTransport(cloudTransport),
    edge_event_sync_policy: {
      retention_seconds: policy.retentionSeconds,
      event_capacity: policy.eventCapacity,
      byte_capacity: policy.byteCapacity,
      snapshot_seconds: policy.snapshotSeconds,
      include_gpio: policy.includeGPIO,
      include_plc: policy.includePLC,
      include_connectors: policy.includeConnectors,
      include_output_evidence: policy.includeOutputEvidence,
    },
    expected_revision: Number.isSafeInteger(expectedRevision) && expectedRevision >= 0
      ? expectedRevision
      : null,
  };
};

export const hardwareIOGatewayIDFromSearch = (search) => {
  try {
    return String(new URLSearchParams(search || "").get("hardware_io") || "").trim();
  } catch {
    return "";
  }
};

export const runtimeConnectionRequestFromSearch = (search) => {
  try {
    const params = new URLSearchParams(search || "");
    const runtimeID = String(params.get("runtime") || params.get("hardware_io") || "").trim();
    if (!runtimeID) return null;
    const requested = String(params.get("connection") || "").trim().toLowerCase();
    return {
      runtimeID,
      connection: requested || (params.has("hardware_io") ? "gpio" : ""),
      legacy: params.has("hardware_io"),
    };
  } catch {
    return null;
  }
};

export const randomInstallID = () => {
  const bytes = new Uint8Array(8);
  try {
    window.crypto?.getRandomValues(bytes);
  } catch {
    return `edge-${Date.now().toString(36)}`;
  }
  return `edge-${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
};

export const browserFallbackCloudURL = () => {
  if (typeof window === "undefined") return CORE_URL;
  const apiURL = new URL(CORE_URL, window.location.href);
  return apiURL.origin;
};

export const isLocalOnlyURL = (value) => {
  try {
    const url = new URL(value);
    return ["localhost", "127.0.0.1", "::1"].includes(url.hostname);
  } catch {
    return false;
  }
};

export const runtimeEnrollmentReadiness = ({
  cloudURL,
  signingLoading,
  signingPolicy,
  packageReadinessLoading = false,
  packageReadiness = null,
}) => {
  if (isLocalOnlyURL(cloudURL)) {
    return { ready: false, title: "Reachable URL required", message: "Localhost points back at the runtime itself." };
  }
  if (signingLoading || signingPolicy?.active !== true) {
    return { ready: false, title: "Deployment security required", message: "Finish deployment security setup before enrolling the first runtime." };
  }
  if (packageReadinessLoading || packageReadiness?.ready !== true) {
    return {
      ready: false,
      title: "Runtime installer unavailable",
      message: "The runtime installer is not ready yet. Try again after the next software update.",
    };
  }
  return { ready: true, title: "", message: "" };
};

// "3m ago" / "2h ago" for recent heartbeats, full date beyond a day.
export const formatLastSeen = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const deltaSec = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (deltaSec < 60) return "just now";
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  return date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
};

export const edgeCommandDeadlineView = (command, now = Date.now()) => {
  if (!command?.expires_at || !["queued", "sent"].includes(command.status)) return null;
  const deadline = new Date(command.expires_at).getTime();
  if (!Number.isFinite(deadline)) {
    return command.status === "sent"
      ? { state: "warning", label: "The device did not confirm this change. Check its current state before retrying" }
      : { state: "warning", label: "This change could not be scheduled. Try again" };
  }
  const remainingMs = deadline - now;
  if (remainingMs <= 0) {
    return command.status === "sent"
      ? { state: "warning", label: "The device may have applied this change. Check its current state before retrying" }
      : { state: "warning", label: "This change expired before it was sent. Try again" };
  }
  if (remainingMs < 60 * 1000) {
    return { state: "pending", label: `Command expires in ${Math.ceil(remainingMs / 1000)}s` };
  }
  if (remainingMs < 60 * 60 * 1000) {
    return { state: "pending", label: `Command expires in ${Math.ceil(remainingMs / (60 * 1000))}m` };
  }
  return {
    state: "pending",
    label: `Command expires ${new Date(deadline).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}`,
  };
};

export const normalizeRuntime = (runtime) => {
  if (!runtime) return {};
  if (typeof runtime === "string") {
    try { return JSON.parse(runtime); } catch { return {}; }
  }
  return runtime;
};

// A replacement setup command must preserve the runtime's existing public
// hardware and synchronization contract. Token rotation changes only the
// credential; silently falling back to the enrollment form defaults can
// detach qualified terminal I/O from the same physical device.
export const runtimeReenrollmentSettings = (gateway) => {
  const runtime = normalizeRuntime(gateway?.runtime);
  // hardware_profile is a required public Gateway field. Presence matters:
  // an explicit empty string is the reviewed profile-removal state. Treat a
  // missing field from a malformed/legacy response as unknown (therefore no
  // profile) instead of reviving an old heartbeat's FR202 observation.
  const hasDurableProfile = gateway != null
    && Object.prototype.hasOwnProperty.call(gateway, "hardware_profile");
  const hardwareProfile = hasDurableProfile
    ? String(gateway.hardware_profile || "").trim()
    : "";
  const durablePolicy = gateway?.edge_event_sync_policy;
  return {
    hardwareProfile,
    cloudTransport: normalizeEdgeCloudTransport(gateway?.cloud_transport),
    syncPolicy: normalizeEdgeSyncPolicy(
      durablePolicy && typeof durablePolicy === "object"
        ? durablePolicy
        : runtime?.edge_event_sync_policy
    ),
  };
};

const GPIO_HEADER40_PINS = Object.freeze([
  [2, 3], [3, 5], [4, 7], [14, 8], [15, 10], [17, 11], [18, 12],
  [27, 13], [22, 15], [23, 16], [24, 18], [10, 19], [9, 21], [25, 22],
  [11, 23], [8, 24], [7, 26], [5, 29], [6, 31], [12, 32], [13, 33],
  [19, 35], [16, 36], [26, 37], [20, 38], [21, 40],
].map(([bcm, physical]) => Object.freeze({ bcm, physical })));

// BCM → physical header pin, so a configured point can show the pin you
// actually wire to without cross-referencing a pinout.
export const GPIO_PHYSICAL_BY_BCM = Object.freeze(
  GPIO_HEADER40_PINS.reduce((acc, pin) => { acc[pin.bcm] = pin.physical; return acc; }, {}),
);

const GPIO_BOARD_CATALOG = Object.freeze({
  "raspberry-pi-4-model-b-40pin-bcm-v1": Object.freeze({
    label: "Raspberry Pi 4 Model B · 40-pin header",
    model: "Raspberry Pi 4 Model B",
    pins: GPIO_HEADER40_PINS,
  }),
  "raspberry-pi-5-40pin-bcm-v1": Object.freeze({
    label: "Raspberry Pi 5 · 40-pin header",
    model: "Raspberry Pi 5 Model B",
    pins: GPIO_HEADER40_PINS,
  }),
  "compulab-iot-link-dio-v1": Object.freeze({
    label: "Compulab IOT-LINK · 24 V digital I/O",
    model: "Compulab IOT-LINK",
    pins: Object.freeze([
      Object.freeze({ bcm: 4, physical: 2, label: "DIO1" }),
      Object.freeze({ bcm: 5, physical: 3, label: "DIO2" }),
      Object.freeze({ bcm: 12, physical: 4, label: "DIO3" }),
    ]),
  }),
});

export const edgeGPIOBoardView = (gateway) => {
  const board = normalizeRuntime(gateway?.runtime)?.gpio_board;
  const profile = String(board?.profile || "");
  const catalog = Object.prototype.hasOwnProperty.call(GPIO_BOARD_CATALOG, profile)
    ? GPIO_BOARD_CATALOG[profile]
    : null;
  const detected = board?.status === "detected" && !!catalog;
  const advertised = detected && Array.isArray(board.available_pins)
    ? new Set(board.available_pins
      .filter((pin) => pin?.policy !== "reserved")
      .map((pin) => Number(pin?.bcm))
      .filter(Number.isInteger))
    : new Set();
  const pins = detected
    ? catalog.pins
      .filter((pin) => advertised.has(pin.bcm))
      .map((pin) => ({
        value: String(pin.bcm),
        label: pin.label ? `${pin.label} — terminal ${pin.physical}` : `GPIO${pin.bcm} — physical pin ${pin.physical}`,
      }))
    : [];
  return {
    detected,
    label: detected ? catalog.label : "Digital I/O not confirmed",
    model: detected ? catalog.model : "",
    pins,
    message: detected
      ? "Compatible built-in digital I/O detected."
      : "A supported digital I/O profile has not been detected.",
  };
};

export const plcDeploymentSecurityView = (gateway) => {
  const runtime = normalizeRuntime(gateway?.runtime);
  const security = runtime?.plc_deployment_security;
  if (!security || typeof security !== "object") {
    return {
      label: "PLC deployment security",
      status: "Not reported",
      pillClass: "isNeutral",
      title: "This device has not reported its PLC deployment security yet.",
      error: "",
    };
  }
  const enforced = security.enforcement_enabled === true;
  const trustedKeys = Array.isArray(security.trusted_key_ids) ? security.trusted_key_ids.filter(Boolean) : [];
  const lastError = typeof security.last_error === "string" && security.last_error.trim()
    ? "The most recent PLC deployment was rejected. Review the application and try again."
    : "";
  if (enforced && trustedKeys.length > 0) {
    return {
      label: "PLC deployment security",
      status: "On",
      pillClass: "isPositive",
      title: "Only verified PLC applications can be installed on this device.",
      error: lastError,
    };
  }
  if (enforced) {
    return {
      label: "PLC deployment security",
      status: "Needs attention",
      pillClass: "isWarning",
      title: "PLC deployment security needs attention before applications can be installed.",
      error: lastError,
    };
  }
  return {
    label: "PLC deployment security",
    status: "Off",
    pillClass: "isWarning",
    title: "This device still accepts unverified PLC applications. Update it before production use.",
    error: lastError,
  };
};

export const fieldbusProtocolLabel = (protocol) => {
  if (protocol === "ethernet_ip_class1") return "EtherNet/IP I/O";
  if (protocol === "ethercat") return "EtherCAT";
  if (protocol === "profinet_io_controller") return "PROFINET";
  if (protocol === "profibus_dp") return "PROFIBUS DP";
  return "";
};

// Classify a runtime's update state for the UI. The server distinguishes three
// cases the old "Update available / Update unavailable" pair conflated:
//   - self:   a newer signed bundle exists AND the device can install it over
//             the air (supports_self_update) → offer the Update button.
//   - manual: a newer runtime exists but this device can't self-update (it
//             predates signed-root OTA, or OTA is disabled) → it updates by
//             re-running the setup command; NOT an error.
//   - paused: the device CAN self-update but something blocks it right now
//             (e.g. running PLC applications) → actionable warning.
// A device that is already current returns mode "none".
export const runtimeUpdateState = (gateway) => {
  const update = gateway?.runtime_update || null;
  const lastSeen = Boolean(gateway?.last_seen_at);
  if (!update || !lastSeen) return { mode: "none", update };
  if (update.updating === true) {
    return {
      mode: "updating",
      update,
      reason: "The runtime is installing the update and may briefly appear offline while it restarts.",
    };
  }
  const available = update.update_available === true;
  if (available && update.supports_self_update === true) {
    return { mode: "self", update, reason: "A newer runtime version is available for this device." };
  }
  if (available && update.supports_self_update !== true) {
    return {
      mode: "manual",
      update,
      reason: "A newer runtime is available. This device updates by re-running the setup command — use Reconnect runtime to get a fresh command.",
    };
  }
  if (update.error) {
    return { mode: "paused", update, reason: "The update is temporarily unavailable." };
  }
  return { mode: "none", update };
};

export const runtimeVersionLabel = (update) => {
  if (!update) return "";
  const version = update.running_version && update.running_version !== "dev" ? update.running_version : "";
  return version;
};

const finiteRuntimeMetric = (value) => {
  if (value == null || value === "" || typeof value === "boolean") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : null;
};

const runtimePercentageMetric = (value) => {
  const numeric = finiteRuntimeMetric(value);
  return numeric !== null && numeric <= 100 ? numeric : null;
};

const runtimeTemperatureMetric = (value) => {
  if (value == null || value === "" || typeof value === "boolean") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= -100 && numeric <= 250 ? numeric : null;
};

export const runtimeDevicePerformance = (runtime) => {
  const performance = normalizeRuntime(runtime)?.device_performance;
  if (!performance || typeof performance !== "object") return null;
  const reportedMemoryTotal = finiteRuntimeMetric(performance.memory_total_bytes);
  const memoryTotal = reportedMemoryTotal > 0 ? reportedMemoryTotal : null;
  const reportedMemoryUsage = finiteRuntimeMetric(performance.memory_usage_bytes);
  const memoryUsage = memoryTotal !== null && reportedMemoryUsage <= memoryTotal
    ? reportedMemoryUsage
    : null;
  // Older runtimes reported every failed /proc read as a good zero. An
  // explicit zero-byte host is impossible, so do not present its matching
  // zero CPU sample as a healthy idle machine.
  const failedHostCollection = reportedMemoryTotal === 0;
  const view = {
    cpuUsage: failedHostCollection ? null : runtimePercentageMetric(performance.cpu_usage_percent),
    cpuTemperature: runtimeTemperatureMetric(performance.cpu_temperature_celsius),
    memoryUsage,
    memoryTotal,
    memoryUtilization: memoryTotal === null ? null : runtimePercentageMetric(performance.memory_utilization_percent),
    diskUtilization: runtimePercentageMetric(performance.disk_utilization_percent),
    diskAvailable: finiteRuntimeMetric(performance.disk_available_bytes),
    uptimeSeconds: finiteRuntimeMetric(performance.uptime_seconds),
  };
  return Object.values(view).some((value) => value !== null) ? view : null;
};

export const formatRuntimeMetricBytes = (bytes) => {
  if (bytes == null || !Number.isFinite(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
};

export const formatRuntimeMetricPercent = (value) => (
  value == null || !Number.isFinite(value) ? "—" : `${value.toFixed(1)}%`
);

export const formatRuntimeTemperature = (value) => (
  value == null || !Number.isFinite(value) ? "—" : `${value.toFixed(1)} °C`
);

export const formatRuntimeUptime = (seconds) => {
  if (seconds == null || !Number.isFinite(seconds)) return "—";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

export const runtimePLCPerformance = (runtime) => {
  const programs = Array.isArray(normalizeRuntime(runtime)?.plc_programs)
    ? normalizeRuntime(runtime).plc_programs
    : [];
  if (programs.length === 0) return null;
  let running = 0;
  let scans = 0;
  let overruns = 0;
  let weightedScanUs = 0;
  let maxScanUs = 0;
  programs.forEach((program) => {
    const status = program?.runtime || {};
    if (status.state === "running") running += 1;
    const programScans = finiteRuntimeMetric(status.scans) || 0;
    const average = finiteRuntimeMetric(status.avg_scan_us) || 0;
    scans += programScans;
    weightedScanUs += average * programScans;
    overruns += finiteRuntimeMetric(status.overruns) || 0;
    maxScanUs = Math.max(maxScanUs, finiteRuntimeMetric(status.max_scan_us) || 0);
  });
  return {
    applications: programs.length,
    running,
    scans,
    overruns,
    averageScanUs: scans > 0 ? weightedScanUs / scans : null,
    maxScanUs: maxScanUs > 0 ? maxScanUs : null,
  };
};

export const formatRuntimeScanDuration = (microseconds) => {
  if (microseconds == null || !Number.isFinite(microseconds)) return "—";
  if (microseconds < 1000) return `${Math.round(microseconds)} µs`;
  if (microseconds < 1000000) return `${(microseconds / 1000).toFixed(1)} ms`;
  return `${(microseconds / 1000000).toFixed(2)} s`;
};

export const plcBuildIdentityView = (program) => {
  const desired = program?.desired || {};
  const actual = program?.actual || {};
  if (!desired.build_manifest_sha256) return null;
  const verified = actual.build_manifest_verified === true &&
    actual.deployment_sha256 === desired.deployment_sha256 &&
    actual.build_manifest_sha256 === desired.build_manifest_sha256 &&
    actual.build_content_sha256 === desired.build_content_sha256;
  if (verified) {
    return {
      state: "verified",
      label: "PLC application verified",
      detail: "The device confirmed this application.",
    };
  }
  const pending = !actual.present || ["pending", "not_downloaded"].includes(program?.sync_state);
  return {
    state: pending ? "pending" : "mismatch",
    label: pending ? "Waiting for device verification" : "PLC application verification failed",
    detail: pending ? "The device has not confirmed this application yet." : "Review the deployment and try again.",
  };
};

const shellQuote = (value) => `'${String(value || "").replaceAll("'", `'"'"'`)}'`;

// The public setup script carries no credential: the server bakes this
// runtime's public endpoint identity into GET /api/edge/setup/{installID}, so
// the operator-facing command stays short. It installs the digest-pinned
// runtime package on a bare supported Linux host, or skips that step on a
// factory image, then prompts for the separately displayed secret.
export const installerCommand = ({ cloudURL, installID, hardwareProfile }) => {
  const server = String(cloudURL || "").trim().replace(/\/$/, "");
  const safeInstall = String(installID || "").trim();
  if (isRutOSHardwareProfile(hardwareProfile)) {
    return `curl -fsS ${shellQuote(`${server}/api/edge/setup-rutos/${safeInstall}`)} | sh`;
  }
  return `curl -fsS ${shellQuote(`${server}/api/edge/setup/${safeInstall}`)} | sudo bash`;
};

export const gpioWriteErrorMessage = (err) => {
  const message = err?.message || "";
  if (/driven by a PLC program/i.test(message)) {
    return "This output is controlled by a running PLC program. Use the PLC Force control, or stop the program before writing manually.";
  }
  return "Could not write the GPIO output. Check the device connection and try again.";
};

export const PLC_SYNC_LABELS = {
  in_sync: "In sync",
  pending: "Pending",
  not_downloaded: "Not downloaded",
  version_mismatch: "Version mismatch",
  run_state_mismatch: "Run state mismatch",
  faulted: "Faulted",
  unmanaged: "Device only",
  command_failed: "Command failed",
  unknown: "State unknown",
};

export const plcSyncPillClass = (state) => {
  if (state === "in_sync") return "isPositive";
  if (["pending", "not_downloaded", "version_mismatch", "run_state_mismatch", "faulted", "command_failed"].includes(state)) return "isWarning";
  return "isNeutral";
};

export const plcCommandLabel = (kind) => {
  if (kind === "plc.deploy") return "deployment";
  if (kind === "plc.set_enabled") return "run-state command";
  return "command";
};

export const runtimeInventoryCounts = (gateway) => {
  const runtime = normalizeRuntime(gateway?.runtime);
  const applications = Array.isArray(runtime.plc_programs) ? runtime.plc_programs.length : null;
  const connections = Array.isArray(runtime.connectors) ? runtime.connectors.length : null;
  return { applications, connections };
};

export const openRuntimeAI = (gateway, task) => {
  window.dispatchEvent(new CustomEvent("gw-open-ai-with-prompt", { detail: {
    prompt: `${task} for runtime ${gateway.name}. Use only capabilities this runtime currently advertises and keep every review and confirmation manual.`,
  } }));
};
